import { DRAWER_WIDTH, useDrawer } from '@app/src/components/Drawer/DrawerContext'
import DrawerViewAiScreenedAnswerDetail from '@app/src/components/Drawer/Views/DrawerViewAiScreenedAnswerDetail'
import type { ResponseItem } from '@app/src/types/resourceExplorer'
import { AutoAwesomeOutlined } from '@mui/icons-material'
import { Stack, Tooltip, Typography, useTheme } from '@mui/material'
import React, { useState } from 'react'
import { useIntl } from 'react-intl'
import TextTruncate from 'react-text-truncate'
import ParentCellOrFragment from './ParentCellOrFragment'

type CommentContentProps = {
  responseItem: ResponseItem
}

const CommentContent: React.FC<CommentContentProps> = ({ responseItem }) => {
  const { formatMessage } = useIntl()
  const { openDrawer } = useDrawer()
  const [truncated, setTruncated] = useState(false)
  const comment = responseItem.comment ?? ''
  const isScraped = responseItem.isScraped === true

  const handleReadMoreClick = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    e.nativeEvent?.stopImmediatePropagation?.()
    openDrawer(<DrawerViewAiScreenedAnswerDetail item={responseItem} />, {
      showAboveDialogs: true,
      maxWidth: DRAWER_WIDTH.wide,
    })
  }

  const tooltipTitle = truncated ? (
    isScraped ? (
      <>
        {comment}
        <br />
        <span
          role="button"
          onClick={handleReadMoreClick}
          style={{ fontWeight: 500, textDecoration: 'underline', cursor: 'pointer' }}
        >
          {formatMessage({ id: 'general.readMore' })}
        </span>
      </>
    ) : (
      comment
    )
  ) : undefined

  return (
    <Tooltip title={tooltipTitle}>
      <Typography variant="body1" component="span">
        <TextTruncate line={2} text={comment} onTruncated={() => setTruncated(true)} />
      </Typography>
    </Tooltip>
  )
}

export type CommentCellProps = {
  responseItem: ResponseItem
}

const CommentCell: React.FC<CommentCellProps> = ({ responseItem }) => {
  const { formatMessage } = useIntl()
  const { palette } = useTheme()

  const hasValue = Boolean(responseItem.comment?.trim())

  const content = !hasValue ? (
    <Typography component="span" color="textSecondary">
      {formatMessage({ id: 'reporting.skipQuestion.noComment' })}
    </Typography>
  ) : responseItem.isScraped ? (
    <Stack direction="row" alignItems="center" gap={1}>
      <AutoAwesomeOutlined sx={{ color: palette.communication.dark }} />
      <CommentContent responseItem={responseItem} />
    </Stack>
  ) : (
    <CommentContent responseItem={responseItem} />
  )

  return <ParentCellOrFragment>{content}</ParentCellOrFragment>
}

export default CommentCell
